import { useLayoutEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import Editor from 'react-simple-code-editor'
import { highlight, languages } from 'prismjs'
import getCaretCoordinates from 'textarea-caret'
import { FormLabel } from '@microservices/wiskey-react-components'
import { Box, useTheme } from '@mui/material'

import { BIND_TYPE, MAX_INPUT_LENGTH, REGEX } from '@constants'

import { ListVariables } from './ListVariables'

interface IProps {
  formId?: string | number
  windowId?: string | number
  name: string
  label?: string
  placeholder?: string
  stylesEditor?: React.CSSProperties
}

export const InputWithListVariables = ({
  formId,
  windowId,
  name,
  label,
  placeholder,
  stylesEditor = {},
}: IProps) => {
  const { setValue, watch } = useFormContext()
  const inputValue = watch(name) ?? ''
  const [isShowList, setIsShowList] = useState(false)
  const [isEditorFocused, setIsEditorFocused] = useState(false)
  const [caretIndex, setCaretIndex] = useState(0)
  const [caretCoordinates, setCaretCoordinates] = useState({ top: 0, left: 0, height: 0 })

  const containerRef = useRef<HTMLDivElement | null>(null)
  const editorRef = useRef<Editor>(null)

  const theme = useTheme()

  const language = BIND_TYPE.JS

  const CUSTOM_KEY_WORDS = [
    ...(formId
      ? [
          {
            pattern: REGEX.FORM_VARIABLE,
            alias: 'form',
          },
        ]
      : []),
    ...(windowId
      ? [
          {
            pattern: REGEX.WINDOW_VARIABLE,
            alias: 'window',
          },
        ]
      : []),
    {
      pattern: REGEX.GLOBAL_VARIABLE,
      alias: 'global',
    },
  ]

  const handleEditorFocus = () => {
    setIsEditorFocused(true)
    setIsShowList(false)
    setCaretIndex(0)
  }

  const handleEditorBlur = () => {
    setIsEditorFocused(false)
    setIsShowList(false)
  }

  const handleChangeCaretIndex = () => {
    if (editorRef.current?._input && caretIndex !== editorRef.current?._input.selectionStart) {
      setCaretIndex(editorRef.current?._input.selectionStart)
    }
  }

  const handleEditorValueChange = (value: string) => {
    setValue(name, value, { shouldDirty: true })
  }

  const highlightCode = (code: string) => {
    const keywords = Array.isArray(languages[language].keyword) ? languages[language].keyword : []

    if (CUSTOM_KEY_WORDS.length !== 0) {
      return highlight(
        code,
        {
          ...languages[language],
          keyword: [...keywords, ...CUSTOM_KEY_WORDS],
        },
        language
      )
    }

    return highlight(code, languages[language], language)
  }

  const checkKeyWordBeforeCaret = () => {
    if (!caretIndex || caretIndex == 0) {
      setIsShowList(false)

      return
    }

    const stringBeforeCaret = inputValue.slice(0, caretIndex)

    const endsWithAlias = CUSTOM_KEY_WORDS.some(customKeyWord => {
      return (
        stringBeforeCaret.endsWith(` ${customKeyWord.alias}.`) ||
        stringBeforeCaret.endsWith(`\n${customKeyWord.alias}.`) ||
        stringBeforeCaret === `${customKeyWord.alias}.`
      )
    })

    if (endsWithAlias) {
      setIsShowList(true)

      return
    }

    setIsShowList(false)
  }

  useLayoutEffect(() => {
    if (!inputValue) setIsShowList(false)
    if (!editorRef?.current?._input || (!caretIndex && caretIndex !== 0) || !inputValue) return

    checkKeyWordBeforeCaret()

    const newCaretCoordinates = getCaretCoordinates(editorRef?.current?._input, caretIndex)

    if (
      newCaretCoordinates?.top !== caretCoordinates?.top ||
      newCaretCoordinates?.left !== caretCoordinates?.left
    )
      setCaretCoordinates(newCaretCoordinates)
  }, [inputValue, caretIndex])

  return (
    <Box sx={{ position: 'relative' }}>
      <FormLabel
        label={label}
        name={name}
        control={
          <Box ref={containerRef} flexGrow={1} sx={{ position: 'relative' }}>
            <Editor
              ref={editorRef}
              highlight={highlightCode}
              maxLength={MAX_INPUT_LENGTH}
              padding={10}
              placeholder={placeholder}
              value={inputValue}
              style={{
                width: '100%',
                border: theme.palette.border.field,
                transition: 'all .5s ease-in-out',
                flexGrow: 1,
                height: 'auto',
                borderRadius: `${theme.shape.borderRadius}px`,
                ...stylesEditor,
              }}
              onBlur={handleEditorBlur}
              onClick={handleChangeCaretIndex}
              onFocus={handleEditorFocus}
              onKeyDown={handleChangeCaretIndex}
              onKeyUp={handleChangeCaretIndex}
              onMouseDown={handleChangeCaretIndex}
              onValueChange={handleEditorValueChange}
            />
            <ListVariables
              caretCoordinates={caretCoordinates}
              caretIndex={caretIndex}
              containerRef={containerRef}
              editorRef={editorRef}
              formId={formId}
              inputValue={inputValue}
              isEditorFocused={isEditorFocused}
              isShowList={isShowList}
              setCaretIndex={setCaretIndex}
              setIsShowList={setIsShowList}
              windowId={windowId}
              onChangeInputValue={handleEditorValueChange}
            />
          </Box>
        }
        sx={{
          marginBottom: '8px',
        }}
      />
    </Box>
  )
}
